import { useCallback, useMemo } from "react";

/** How long a page+scope key's fresh-on-entry claim holds before the next check is allowed to
 *  bypass the server cache again. Keeps SPA back-and-forth navigation (and company-to-company
 *  switching) from thrashing the single shared App Runner cache, while "I fixed Airtable and
 *  came back" (after this window elapses, or after a hard reload) always gets one fresh read. */
const FRESHNESS_FLOOR_MS = 60_000;

/** Module-level so the floor holds across component unmount/remount within one SPA session
 *  (route away and back, or switch company and back) — a hard page reload clears it (a fresh
 *  module load), which is the desired behavior: a fresh page load always gets one fresh read.
 *  Not exported directly; tests use distinct `pageKey`s and/or `resetFreshnessFloor()` rather
 *  than reaching into this map. */
const lastFreshAtByPageKey = new Map<string, number>();

/** Escape hatches from the floor: logout, an impersonation switch, and the "Clear cache"
 *  gesture all change what the next read *should* mean "fresh" relative to, without a hard
 *  reload to clear the module scope for us. Called from `Logout.vm.ts`,
 *  `ImpersonationContext.vm.ts`, and `DrawerNavigation.vm.ts`'s `handleClearCache`. */
export function resetFreshnessFloor(): void {
	lastFreshAtByPageKey.clear();
}

function floorKey(pageKey: string, scopeKey?: string): string {
	return scopeKey === undefined ? pageKey : `${pageKey}:${scopeKey}`;
}

/**
 * The freshness floor behind the cache-bypassing `fresh` flag threaded into the data-heavy
 * pages' scoped reads (Jobs Dashboard, Submit Jobs, Mark Jobs Ready, Company Settings): at most
 * one fresh read per `pageKey` (optionally narrowed by a `scopeKey`, e.g. the currently-selected
 * company's id) every 60 seconds.
 *
 * Split into a query and a command rather than one function that does both, so a failed fetch
 * doesn't burn the claim: call `shouldFetchFresh(scopeKey)` to decide whether to ask the server
 * for a fresh read, and call `commitFresh(scopeKey)` yourself once that read actually succeeds.
 * Calling `commitFresh` after a rejected request would floor every retry for the next 60s at
 * exactly the moment the user is trying to escape stale data.
 *
 * Company selection is GLOBAL (the persistent Toolbar dropdown writes `CompanyContext`), so a
 * page's company-scoped effect can refire many times over a single mount — once per company an
 * admin switches to. Evaluate `shouldFetchFresh` *from inside* the effect that reacts to the
 * change, passing the new scope (company id) each time, rather than hoisting a single evaluation
 * into a variable — otherwise every company switched to for the rest of the visit would reuse
 * whichever answer the first company happened to get.
 *
 * `now` is for tests only; production callers should omit it.
 */
export function useFreshOnEntry(
	pageKey: string,
	now: () => number = Date.now,
): { shouldFetchFresh: (scopeKey?: string) => boolean; commitFresh: (scopeKey?: string) => void } {
	const shouldFetchFresh = useCallback(
		(scopeKey?: string) => {
			const lastFreshAt = lastFreshAtByPageKey.get(floorKey(pageKey, scopeKey));
			return lastFreshAt === undefined || now() - lastFreshAt >= FRESHNESS_FLOOR_MS;
		},
		[pageKey, now],
	);

	const commitFresh = useCallback(
		(scopeKey?: string) => {
			lastFreshAtByPageKey.set(floorKey(pageKey, scopeKey), now());
		},
		[pageKey, now],
	);

	return useMemo(() => ({ shouldFetchFresh, commitFresh }), [shouldFetchFresh, commitFresh]);
}
